// <wd-calculator>: ölçü girişi ve fiyat. PDP, Design Studio, See It On My Wall ve
// anasayfa demosunda aynı bileşen (README "Panel plan, always explained").
// Matematik yalnız walldora-area.js içinde; bu dosya hesap yapmaz, çağırır.
//
// Kilitli davranışlar (handoff §7.2):
//   inputlar tek doğruluk kaynağı · sayfa Feature 150 × 96 ile dolu fiyatla açılır ·
//   custom durumu onaydır · hazır ölçü aktifken hesap kanonik inç değerinden yapılır
//   (cm turu fiyatı kaydırmaz) · ft + in ayrı kutu, metrikte tek kutu · rakamlar tabular
//
// Çıkış: her değişiklikte "wd:calc" olayı, detail aşağıdaki snapshot().
//
// İsteğe bağlı (yalnız işaretleme varsa): [data-wd-size-custom] "Custom size", seçilince hazır
// ölçü bırakılır ve genişlik kutusuna odaklanılır. [data-wd-material-rate] metrikte $/m² yazar.
// [data-wd-size-sample] numune seçeneği, seçilince
// [data-wd-wall-only] gizlenir, [data-wd-sample-only] görünür, snapshot.sample true ve toplam
// numune fiyatı olur (DURUM "Numune"). [data-wd-preset-price] hazır ölçünün o malzemedeki fiyatı.

import { area, total, panelPlan, planLabel, MIN_AREA } from "walldora-area";

// Para biçimi: sayfanın para birimi, Liquid'le aynı biçim (assets/money.js, snippets/money.liquid). walldora-area'daki
// money() hep "$" yazar; o dosya kaynaktan kopya olduğu için değiştirilmez (CLAUDE.md). ABD'de çıktı aynı: $1,234.00.
// Tutar zaten sayfanın para biriminde gelir (data-price, Shopify'ın çevirdiği varyant fiyatı)
import { money } from "walldora-money";

// $20 alt sınırı (walldora-area MIN_ORDER) dolar cinsinden: yerel varyant fiyatı Shopify'ın kuruyla dolara çevrilip
// total()'e verilir, tutar yerel paraya döner ve kuruşa yuvarlanır (2026-11-04: euroda 6 sq ft €19.02 iken hesaplayıcı
// "Minimum order applied" deyip €20.00 gösteriyor, sepet €19.02 alıyordu). Dolarda kur 1, sonuç aynı
function localTotal(sqft, rate) {
  const fx = Number(window.Shopify?.currency?.rate) || 1;
  const t = total(sqft, rate / fx);
  return { value: Math.round(t.value * fx * 100) / 100, note: t.note };
}

// Hazır ölçüler kanonik inç (handoff §1.2). Veri, matematik değil.
const PRESETS = [
  { key: "feature", w: 150, h: 96 },
  { key: "standard", w: 125, h: 96 },
  { key: "half", w: 75, h: 96 },
  { key: "chimney", w: 60, h: 108 },
];
const CM_PER_IN = 2.54;
const PRESET_TOLERANCE_IN = 0.5;
const UNIT_COOKIE = "wd_unit";

// m² başına fiyat yalnız görüntü: ücret her zaman walldora-area'nın sq ft hesabıyla çıkar
const SQFT_PER_M2 = 10.7639;
// Metrik gösterim (hesap değil): walldora-area inç ve sq ft üretir, cm seçiliyse ekranda cm ve m²
const cm = (inches) => Math.round(inches * CM_PER_IN);
const metricText = (text) => String(text).replace(/(\d+(?:\.\d+)?)"/g, (_, n) => `${cm(Number(n))} cm`);
// 150 > 12' 6" (görüntü biçimi, hesap değil)
const ftIn = (inches) => {
  const r = Math.round(inches);
  return `${Math.floor(r / 12)}' ${r % 12}"`;
};
const readCookie = (name) => document.cookie.split("; ").find((c) => c.startsWith(name + "="))?.split("=")[1];
const writeCookie = (name, value) => {
  document.cookie = `${name}=${value}; path=/; max-age=31536000; samesite=lax`;
};
const num = (value) => {
  const n = parseFloat(String(value).replace(",", "."));
  return Number.isFinite(n) && n > 0 ? n : 0;
};

class WdCalculator extends HTMLElement {
  connectedCallback() {
    this.materials = [...this.querySelectorAll("[data-wd-material]")].map((el) => ({
      el,
      id: el.dataset.id,
      name: el.dataset.name,
      rate: Number(el.dataset.price) / 100,
    }));
    this.presetButtons = [...this.querySelectorAll("[data-wd-preset]")];
    this.unitButtons = [...this.querySelectorAll("[data-wd-unit]")];
    this.fields = Object.fromEntries(
      [...this.querySelectorAll("[data-wd-field]")].map((el) => [el.dataset.wdField, el])
    );
    this.imperialOnly = [...this.querySelectorAll("[data-wd-imperial-only]")];
    this.unitLabels = [...this.querySelectorAll("[data-wd-unit-label]")];
    this.confirm = this.querySelector("[data-wd-confirm]");
    this.error = this.querySelector("[data-wd-error]");
    this.result = this.querySelector("[data-wd-result]");
    this.sampleButton = this.querySelector("[data-wd-size-sample]");
    this.customButton = this.querySelector("[data-wd-size-custom]");
    this.wallOnly = [...this.querySelectorAll("[data-wd-wall-only]")];
    this.sampleOnly = [...this.querySelectorAll("[data-wd-sample-only]")];

    const params = new URLSearchParams(location.search);
    const fromUrl = { w: num(params.get("w")), h: num(params.get("h")) };
    const variant = params.get("variant");

    this.state = {
      unit: readCookie(UNIT_COOKIE) || this.dataset.defaultUnit || "imperial",
      material: this.materials.find((m) => m.id === variant) || this.materials[0],
      wIn: fromUrl.w || PRESETS[0].w,
      hIn: fromUrl.h || PRESETS[0].h,
      preset: null,
      sample: Boolean(this.sampleButton) && params.get("sample") === "1",
    };
    this.state.preset = this.matchPreset(this.state.wIn, this.state.hIn);
    if (this.state.preset) Object.assign(this.state, { wIn: this.state.preset.w, hIn: this.state.preset.h });

    this.materials.forEach((m) => m.el.addEventListener("click", () => this.update({ material: m })));
    this.presetButtons.forEach((b) =>
      b.addEventListener("click", () => {
        const p = PRESETS.find((x) => x.key === b.dataset.wdPreset);
        this.update({ wIn: p.w, hIn: p.h, preset: p, sample: false, pristine: false }, { writeInputs: true });
      })
    );
    this.sampleButton?.addEventListener("click", () => this.update({ sample: true }));
    this.customButton?.addEventListener("click", () => {
      // Hazır ölçüden ya da numuneden gelince kutular boşalır; yazana kadar hata gösterilmez.
      // Zaten kendi ölçüsündeyse girdiği değerler kalır
      const fresh = this.state.preset || this.state.sample;
      this.update(fresh ? { preset: null, sample: false, wIn: 0, hIn: 0, pristine: true } : { sample: false }, { writeInputs: Boolean(fresh) });
      // Liste kapanınca odak genişliğe (pdp.js listeyi kapatır, sonra odak buraya)
      requestAnimationFrame(() => {
        this.fields.wA.focus();
        this.fields.wA.select();
      });
    });
    this.unitButtons.forEach((b) =>
      b.addEventListener("click", () => {
        writeCookie(UNIT_COOKIE, b.dataset.wdUnit);
        this.update({ unit: b.dataset.wdUnit }, { writeInputs: true });
      })
    );
    Object.values(this.fields).forEach((input) => input.addEventListener("input", () => this.readInputs()));

    this.update({}, { writeInputs: true });
  }

  matchPreset(wIn, hIn) {
    return PRESETS.find((p) => Math.abs(p.w - wIn) < PRESET_TOLERANCE_IN && Math.abs(p.h - hIn) < PRESET_TOLERANCE_IN) || null;
  }

  // Girilen değerler inç'e çevrilir. Hazır ölçüye denk geliyorsa kanonik değer kullanılır.
  readInputs() {
    const f = this.fields;
    const wIn = this.state.unit === "imperial" ? num(f.wA.value) * 12 + num(f.wB.value) : num(f.wA.value) / CM_PER_IN;
    const hIn = this.state.unit === "imperial" ? num(f.hA.value) * 12 + num(f.hB.value) : num(f.hA.value) / CM_PER_IN;
    const preset = this.matchPreset(wIn, hIn);
    // Sessizlik iki ölçü de bir kez girilene kadar sürer: genişliği yazan, yüksekliğe geçmeden uyarı görmez
    const pristine = Boolean(this.state.pristine) && !(wIn > 0 && hIn > 0);
    this.update(preset ? { wIn: preset.w, hIn: preset.h, preset, sample: false, pristine: false } : { wIn, hIn, preset: null, sample: false, pristine });
  }

  writeInputs() {
    const { unit, wIn, hIn } = this.state;
    const f = this.fields;
    if (unit === "imperial") {
      const split = (inches) => {
        const ft = Math.floor(Math.round(inches) / 12);
        return [ft, Math.round(inches) - ft * 12];
      };
      [f.wA.value, f.wB.value] = wIn ? split(wIn) : ["", ""];
      [f.hA.value, f.hB.value] = hIn ? split(hIn) : ["", ""];
    } else {
      f.wA.value = wIn ? Math.round(wIn * CM_PER_IN) : "";
      f.hA.value = hIn ? Math.round(hIn * CM_PER_IN) : "";
    }
  }

  update(patch, { writeInputs = false } = {}) {
    Object.assign(this.state, patch);
    if (writeInputs) this.writeInputs();
    this.render();
    this.dispatchEvent(new CustomEvent("wd:calc", { bubbles: true, detail: this.snapshot() }));
  }

  snapshot() {
    const { unit, wIn, hIn, preset, material, sample } = this.state;
    if (sample) {
      const value = Number(this.sampleButton.dataset.price) / 100;
      return {
        valid: true,
        sample: true,
        sampleId: this.sampleButton.dataset.wdSizeSample,
        unit,
        wIn,
        hIn,
        sqft: 0,
        plan: null,
        planText: "",
        total: value,
        totalText: money(value),
        note: null,
        rateText: money(material.rate),
        material: { id: material.id, name: material.name, rate: material.rate },
        presetKey: null,
        sizeText: this.sizeText(),
        ...this.sideTexts(),
      };
    }
    const valid = wIn > 0 && hIn > 0;
    const sqft = valid ? area(wIn, hIn) : 0;
    const price = valid ? localTotal(sqft, material.rate) : null;
    const plan = valid ? panelPlan(wIn) : null;
    return {
      sample: false,
      valid,
      unit,
      wIn,
      hIn,
      sqft,
      plan,
      planText: valid ? planLabel(wIn) : "",
      ...this.unitTexts(sqft, plan, hIn),
      total: price ? price.value : 0,
      totalText: price ? money(price.value) : money(0),
      // Minimum uyarısı yalnız $20 alt sınırında değil, alan en küçük alana (MIN_AREA) yuvarlandığında da (Fatih
      // 2026-11-04: ft yerine inç ya da cm giren müşteri bütün duvarı o fiyata kaplatacağını sanıyor); metin dil dosyasından
      note: price && (price.note || sqft === MIN_AREA) ? this.dataset.minNote || price.note || "Minimum order applied" : null,
      rateText: money(material.rate),
      material: { id: material.id, name: material.name, rate: material.rate },
      presetKey: preset ? preset.key : null,
      sizeText: this.sizeText(),
      ...this.sideTexts(),
    };
  }

  // Seçili birimde alan, panel düzeni, panel ve artan genişliği, baskı yüksekliği (gösterim)
  unitTexts(sqft, plan, hIn) {
    const metric = this.state.unit === "metric";
    const len = (inches) => (metric ? `${cm(inches)} cm` : `${inches}"`);
    const planText = plan ? planLabel(this.state.wIn) : "";
    return {
      areaText: metric ? `${(sqft / SQFT_PER_M2).toFixed(1)} m²` : `${sqft} sq ft`,
      planDisplay: metric ? metricText(planText) : planText,
      panelWText: plan ? len(plan.panelW) : "",
      restText: plan ? len(plan.restIn) : "",
      printHeightText: metric ? `${cm(Math.ceil(hIn))} cm` : `${Math.ceil(hIn)}"`,
    };
  }

  // Genişlik ve yükseklik ayrı: "12' 6"" ve "8' 0"" ya da "381 cm" ve "244 cm"
  sideTexts() {
    const { unit, wIn, hIn } = this.state;
    const one = (inches) => (unit === "imperial" ? ftIn(inches) : `${Math.round(inches * CM_PER_IN)} cm`);
    return { widthText: one(wIn), heightText: one(hIn) };
  }

  sizeText() {
    const { unit, wIn, hIn } = this.state;
    if (unit === "imperial") return `${ftIn(wIn)} × ${ftIn(hIn)}`;
    return `${Math.round(wIn * CM_PER_IN)} × ${Math.round(hIn * CM_PER_IN)} cm`;
  }

  render() {
    const s = this.snapshot();
    const { unit, material, preset } = this.state;

    const sample = s.sample;
    this.materials.forEach((m) => {
      m.el.setAttribute("aria-pressed", String(m === material));
      const rateEl = m.el.querySelector("[data-wd-material-rate]");
      if (rateEl) {
        rateEl.textContent = unit === "metric"
          ? rateEl.dataset.m2.replace("%price", money(m.rate * SQFT_PER_M2))
          : rateEl.dataset.sqft;
      }
    });
    this.customButton?.setAttribute("aria-pressed", String(!sample && !preset));
    // Custom'ın fiyat alanı: kendi ölçüsündeyken toplam, değilken seçili malzemenin birim fiyatı
    const customPrice = this.customButton?.querySelector("[data-wd-custom-price]");
    if (customPrice) {
      customPrice.textContent = !sample && !preset && s.valid
        ? s.totalText
        : material.el.querySelector("[data-wd-material-rate]")?.textContent ?? "";
    }
    this.presetButtons.forEach((b) => {
      const p = PRESETS.find((x) => x.key === b.dataset.wdPreset);
      b.setAttribute("aria-pressed", String(!sample && preset === p));
      const sizeEl = b.querySelector("[data-wd-preset-size]");
      // data-format="ftin": seçim satırıyla aynı biçim (12' 6" × 8' 0"); çiplerde inç
      sizeEl.textContent = unit !== "imperial"
        ? `${Math.round(p.w * CM_PER_IN)} × ${Math.round(p.h * CM_PER_IN)} cm`
        : sizeEl.dataset.format === "ftin" ? `${ftIn(p.w)} × ${ftIn(p.h)}` : `${p.w}″ × ${p.h}″`;
      const priceEl = b.querySelector("[data-wd-preset-price]");
      if (priceEl) priceEl.textContent = money(localTotal(area(p.w, p.h), material.rate).value);
    });
    this.sampleButton?.setAttribute("aria-pressed", String(sample));
    this.wallOnly.forEach((el) => (el.hidden = sample));
    this.sampleOnly.forEach((el) => (el.hidden = !sample));
    if (sample) return;
    this.unitButtons.forEach((b) => b.setAttribute("aria-pressed", String(b.dataset.wdUnit === unit)));
    this.imperialOnly.forEach((el) => (el.hidden = unit !== "imperial"));
    this.unitLabels.forEach((el) => (el.textContent = unit === "imperial" ? el.dataset.ft : el.dataset.cm));
    ["wA", "hA"].forEach((k) => {
      const f = this.fields[k];
      f.setAttribute("aria-label", unit === "imperial" ? f.dataset.labelFt : f.dataset.labelCm);
    });
    // Custom ile boşaltılan kutular yazılana kadar hata göstermez
    const quiet = this.state.pristine && !s.valid;
    Object.values(this.fields).forEach((f) => f.setAttribute("aria-invalid", String(!s.valid && !quiet)));

    this.error.hidden = s.valid || quiet;
    this.confirm.hidden = !s.valid;
    if (s.valid) {
      const label = preset
        ? this.querySelector(`[data-wd-preset="${preset.key}"] [data-wd-preset-name]`).textContent
        : this.confirm.dataset.custom;
      this.confirm.querySelector("[data-wd-confirm-size]").textContent = label;
    }

    const r = this.result;
    r.hidden = !s.valid;
    if (s.valid) {
      r.querySelector("[data-wd-out='area']").textContent = s.areaText;
      // Artan panel --wd-accent-subtle ile vurgulanır: "6 × 25″ + [1 × 12″]" (cm'de "6 × 64 cm")
      const panels = r.querySelector("[data-wd-out='panels']");
      const [full, rest] = s.planDisplay.split(" + ");
      panels.replaceChildren(full);
      if (rest) {
        const mark = document.createElement("span");
        mark.className = "wd-result__remainder";
        mark.textContent = rest;
        panels.append(" + ", mark);
      }
      r.querySelector("[data-wd-out='height']").textContent = s.printHeightText;
      r.querySelector("[data-wd-out='material']").textContent = material.name;
      r.querySelector("[data-wd-out='rate']").textContent = s.rateText;
      r.querySelector("[data-wd-out='total']").textContent = s.totalText;
      const note = r.querySelector("[data-wd-out='note']");
      note.hidden = !s.note;
      note.textContent = s.note || "";
    }
  }
}

customElements.define("wd-calculator", WdCalculator);
